import React from 'react';
import DatePicker from 'react-datepicker';
import { useTranslation } from 'react-i18next';
import { format, parse } from 'date-fns';
import { de, enUS, fr, ru, pl } from 'date-fns/locale';
import "react-datepicker/dist/react-datepicker.css";
import "../../styles/datepicker.css";

interface LocalizedDatePickerProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  className?: string;
  id?: string;
  name?: string;
}

// Mapping der Sprachen zu date-fns locales
const localeMap = {
  de: de,
  en: enUS,
  fr: fr,
  ru: ru,
  pl: pl,
  ro: enUS, // Fallback für Rumänisch
};

// Lokalisierte Texte für den DatePicker
const getLocalizedTexts = (language: string) => {
  const texts = {
    de: {
      clearButtonTitle: "Löschen",
      todayButtonTitle: "Heute",
      previousMonthButtonLabel: "Vorheriger Monat",
      nextMonthButtonLabel: "Nächster Monat",
      monthSelectLabel: "Monat wählen",
      yearSelectLabel: "Jahr wählen",
    },
    en: {
      clearButtonTitle: "Clear",
      todayButtonTitle: "Today", 
      previousMonthButtonLabel: "Previous month",
      nextMonthButtonLabel: "Next month",
      monthSelectLabel: "Select month",
      yearSelectLabel: "Select year",
    },
    fr: {
      clearButtonTitle: "Effacer",
      todayButtonTitle: "Aujourd'hui",
      previousMonthButtonLabel: "Mois précédent",
      nextMonthButtonLabel: "Mois suivant",
      monthSelectLabel: "Sélectionner le mois",
      yearSelectLabel: "Sélectionner l'année",
    },
    ru: {
      clearButtonTitle: "Очистить",
      todayButtonTitle: "Сегодня",
      previousMonthButtonLabel: "Предыдущий месяц",
      nextMonthButtonLabel: "Следующий месяц",
      monthSelectLabel: "Выбрать месяц",
      yearSelectLabel: "Выбрать год",
    },
    pl: {
      clearButtonTitle: "Wyczyść",
      todayButtonTitle: "Dziś",
      previousMonthButtonLabel: "Poprzedni miesiąc",
      nextMonthButtonLabel: "Następny miesiąc",
      monthSelectLabel: "Wybierz miesiąc",
      yearSelectLabel: "Wybierz rok",
    },
    ro: {
      clearButtonTitle: "Șterge",
      todayButtonTitle: "Astăzi",
      previousMonthButtonLabel: "Luna precedentă",
      nextMonthButtonLabel: "Luna următoare",
      monthSelectLabel: "Selectează luna",
      yearSelectLabel: "Selectează anul",
    }
  };

  return texts[language as keyof typeof texts] || texts.en;
};

const LocalizedDatePicker: React.FC<LocalizedDatePickerProps> = ({
  value,
  onChange,
  placeholder,
  required = false,
  className = "mt-1 block w-full px-3 py-2 border border-strong bg-surface-inset text-primary rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm",
  id,
  name
}) => {
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const locale = localeMap[currentLanguage as keyof typeof localeMap] || enUS;
  const localizedTexts = getLocalizedTexts(currentLanguage);

  // Konvertiere String zu Date
  const dateValue = value ? parse(value, 'yyyy-MM-dd', new Date()) : null;

  // Handle date change
  const handleDateChange = (date: Date | null) => {
    if (date) {
      const formattedDate = format(date, 'yyyy-MM-dd');
      onChange(formattedDate);
    } else {
      onChange('');
    }
  };

  // Custom Input Component mit eigenem Clear Button
  const CustomInput = React.forwardRef<HTMLInputElement, any>((props, ref) => (
    <div className="relative">
      <input
        {...props}
        ref={ref}
        className={className}
        placeholder={placeholder}
        required={required}
        id={id}
        name={name}
      />
      {props.value && (
        <button
          type="button"
          onClick={() => onChange('')}
          className="absolute right-1 top-1/2 transform -translate-y-1/2 w-3 h-3 md:w-4 md:h-4 bg-gray-500 hover:bg-red-500 text-white rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
          title={localizedTexts.clearButtonTitle}
        >
          <span className="text-[6px] md:text-[8px] leading-none font-bold">✕</span>
        </button>
      )}
    </div>
  ));

  // Custom Header mit lokalisierten Buttons
  const CustomHeader = ({
    date,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
  }: any) => (
    <div className="flex justify-between items-center px-2 py-2 bg-surface-raised text-primary">
      <button
        onClick={decreaseMonth}
        disabled={prevMonthButtonDisabled}
        type="button"
        className="p-1 hover:bg-surface-inset rounded disabled:opacity-50"
        title={localizedTexts.previousMonthButtonLabel}
      >
        ←
      </button>
      <div className="font-medium">
        {format(date, 'MMMM yyyy', { locale })}
      </div>
      <button
        onClick={increaseMonth}
        disabled={nextMonthButtonDisabled}
        type="button"
        className="p-1 hover:bg-surface-inset rounded disabled:opacity-50"
        title={localizedTexts.nextMonthButtonLabel}
      >
        →
      </button>
    </div>
  );

  return (
    <div className="relative">
      <DatePicker
        selected={dateValue}
        onChange={handleDateChange}
        locale={locale}
        dateFormat="dd.MM.yyyy"
        customInput={<CustomInput />}
        renderCustomHeader={CustomHeader}
        showPopperArrow={false}
        popperClassName="z-50"
        calendarClassName="bg-surface-raised border border-strong rounded-lg shadow-lg"
        dayClassName={(date) => 
          "hover:bg-indigo-600 hover:text-[#0C1322] cursor-pointer p-1 text-center text-white"
        }
        weekDayClassName={() => "text-muted text-center p-1 font-medium"}
        monthClassName={() => "text-primary"}
        todayButton={
          <div className="border-t border-strong pt-2 mt-2">
            <button
              type="button"
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-[#0C1322] px-3 py-2 rounded text-sm"
            >
              {localizedTexts.todayButtonTitle}
            </button>
          </div>
        }
        isClearable={false}
      />
      

    </div>
  );
};

export default LocalizedDatePicker;
